import React, {
    useReducer, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import EmailPreviewTemplate from './EmailPreviewTemplate';
import { assetUrl } from '../config/asset-config';

const LivePreview = ({
    pageName = '', landingProps = {}, valid, emailMessage = '', ctaDesc = '', src = null, step, submitting, existingLandingIdString, currentProject = {}, envelope = {}, envelopeName, template, displayFont, emailTemplate, emailLogo, business = {}, trans = {}, landingPage = {}, vrExampleVideo, applyExternalStyle = false, recipients = [{
        id: 1,
        title: 'Mr.',
        name: 'John',
        last_name: 'Smith',
        notes: 'Recipient Notes',
        full_name: 'John Smith',
    }], recipientIndex = 0, withFieldsMerged = () => { }, selectRecipient = () => { }, saveLanding = () => { },
}) => {
    const containerRef = useRef(null);
    const [localState, setLocalState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
        loading: false,
        previewSizes: [
            {
                previewWidth: 1440, iconClass: 'fa-desktop', active: true, label: 'desktop',
            },
            {
                previewWidth: 768, iconClass: 'fa-tablet-alt', active: false, label: 'tablet',
            },
            {
                previewWidth: 375, iconClass: 'fa-mobile-alt', active: false, label: 'mobile',
            },
        ],
        scaleAmount: 1,
        projectType: null,
        stepNumber: 0,
    });
    const {
        loading, previewSizes, scaleAmount, projectType, stepNumber,
    } = localState;
    const [activeRecipient, setActiveRecipient] = useState({});

    const activeSize = () => previewSizes.find((s) => s.active);

    const activePreviewStyles = (() => {
        return {
            transform: `scale(${scaleAmount})`,
            width: `${activeSize().previewWidth}px`,
            height: `${(1 / scaleAmount) * 100}%`,
        };
    })();

    const envelopePath = () => currentProject?.envelope?.path;

    const headerLogoDisplay = () => {
        // if we have neither a logo file, or a saved logo image, return an empty string
        // this occurs when creating a brand new page
        // if we don't return an empty string, we end up trying to request
        // a malformed url, resulting in an uncaught error at render
        if (
            landingProps.header_logo === null
            && landingProps.header_logo_file === null
        ) {
            return '';
        }
        // newly uploaded image data takes priority
        if (landingProps.header_logo_file) {
            return landingProps.header_logo_file;
        }
        // work out the existing image path
        // local and qa environments save these images in the main app public directory
        // in prod we save the full url and don't need this prefix so we default to empty string
        let imgPathPrefix = '';
        if (config.env !== 'production') {
            // sometimes the env variable includes protocol, sometimes it doesn't, check for it
            imgPathPrefix = config.tvMainAppUrl ? `${config.tvMainAppUrl.indexOf('http') !== -1 ? '' : 'https://'
            }${config.tvMainAppUrl}` : '';
        }
        return `${imgPathPrefix}${landingProps.header_logo}`;
    };

    useEffect(() => {
        setActiveRecipient(recipients[recipientIndex]);
    }, [recipients]);

    useEffect(() => {
        // Check if currentProject has changed deeply
        if (src) {
            setLocalState({ loading: true });
        }

        // Check if src has changed
        if (src !== null) {
            window.addEventListener('message', setLoadingState);
            // setup watchers for setting preview scaling
            // use resize observer if available (more efficient)
            if (typeof window.ResizeObserver === 'function') {
                const resizeObserver = new ResizeObserver((entries) => {
                    for (const entry of entries) {
                        if (entry.contentRect) {
                            const width = entry.contentRect.width;
                            // calculate transform scale
                            setPreviewScale(width);
                        }
                    }
                });
                resizeObserver.observe(
                    containerRef.current,
                );
            } else {
                window.addEventListener('resize', setPreviewScale);
            }
        }
        return () => {
            if (src !== null && typeof window.ResizeObserver !== 'function') {
                window.removeEventListener('resize', setPreviewScale);
            }
        };
    }, [src]);

    useEffect(() => {
        if (currentProject) {
            setPreviewInfo();
            if (currentProject.method && currentProject.method.slug === 'text') {
                // Set preview size to the first one in the updated array
                applyExternalStyle ? setPreviewSize(previewSizes[1]) : setPreviewSize(previewSizes[0]);
            }
        }
    }, [currentProject]);

    useEffect(() => {
        if (ctaDesc) setPreviewInfo();
    }, [ctaDesc]);

    const changeStep = (number) => {
        setLocalState({ stepNumber: number });
    };

    const scrollRecipient = (num) => {
        // increment active recipient index with wraparound
        let nextRecipientIndex = recipientIndex + num;
        if (nextRecipientIndex > recipients.length - 1) {
            nextRecipientIndex = 0;
        } else if (nextRecipientIndex < 0) {
            nextRecipientIndex = recipients.length - 1;
        }
        // broadcast that we selected a new recipient
        selectRecipient(recipients[nextRecipientIndex].id);
        setActiveRecipient(recipients[nextRecipientIndex]);
    };

    const setLoadingState = (event) => {
        if (event.data === 'landing-loaded') {
            setLocalState({ loading: false });
            selectRecipient(recipients[recipientIndex].id);
        }
    };

    const setPreviewInfo = () => {
        // we can't update givebutter pages in realtime, so just silently return
        if (currentProject.landing_page_module === 'thankgiving') return;
        const previewFrame = document.getElementById('preview-frame');
        // envelope name, project desc, and video thumb.
        let previewWindow = null;
        if (previewFrame && previewFrame.contentWindow) {
            previewWindow = previewFrame.contentWindow;
        }
        if (previewWindow !== null) {
            if (previewWindow.updateEnvelopeTo) {
                previewWindow.updateEnvelopeTo(
                    currentProject.envelopeTo,
                    currentProject.envelope_font_color,
                );
            }
            if (
                previewWindow.updateEnvelopeImage
                && currentProject.envelope
            ) {
                previewWindow.updateEnvelopeImage(
                    `${currentProject.envelope.path}/front.jpg`,
                );
            }
            if (previewWindow.updateProjectDesc) {
                previewWindow.updateProjectDesc(currentProject.cta_desc);
            }
            if (previewWindow.updateThumbnail) {
                const videoThumb = activeRecipient.video_thumb
                    ? activeRecipient.video_thumb
                    : assetUrl('assets/img/video_email_example_plain.jpg');
                previewWindow.updateThumbnail(videoThumb);
            }
            if (previewWindow.updateCtaButton) {
                let btnMarkup = '';
                if (
                    currentProject.cta_name
                    && currentProject.cta_link
                ) {
                    btnMarkup = `<a class="btn btn--large btn--primary cta-btn" href="${currentProject.cta_link}">${currentProject.cta_name}</a>`;
                }
                previewWindow.updateCtaButton(btnMarkup);
            }
            if (
                previewWindow.updateEmbedCode
                && currentProject.landing_page_module === 'embed'
            ) {
                previewWindow.updateEmbedCode(currentProject.embed_code);
            }
            if (previewWindow.updateSecondaryButtons) {
                const showReply = !currentProject.disable_replies_text
                    || !currentProject.disable_replies_video;
                const showDownload = !currentProject.disable_downloads;
                const showShare = !currentProject.disable_shares;
                previewWindow.updateSecondaryButtons(
                    showReply,
                    showDownload,
                    showShare,
                );
            }
            // for video requests
            if (previewWindow.updateRequestFor) {
                previewWindow.updateRequestFor(activeRecipient);
            }
            // for odder
            if (
                currentProject && currentProject.project_type && currentProject.project_type.slug === 'odder'
                && previewWindow.previewOdderReport
            ) {
                previewWindow.previewOdderReport(
                    activeRecipient,
                    currentProject.odder_password,
                );
            }
        }

        // setting projectType because currentProject is initially empty
        if (currentProject && currentProject.project_type && currentProject.project_type.slug === 'video-request') {
            setLocalState({ projectType: 'video-request' });
        }
    };

    const setPreviewSize = (size) => {
        // reset all sizes to inactive
        previewSizes.forEach((s) => {
            s.active = false;
        });
        // mark selected size as active
        size.active = true;
        setLocalState({ previewSizes });
        // wait a tick, then calculate scale of the preview element
        setTimeout(() => setPreviewScale(), 0);
    };

    const setPreviewScale = (referenceWidth) => {
        let refWidth = referenceWidth;
        if (!refWidth) {
            refWidth = containerRef.current.offsetWidth;
        }
        setLocalState({
            scaleAmount: Math.min(
                refWidth / activeSize().previewWidth,
                1,
            ),
        });
    };

    const handleOptionSelection = (event) => {
        const key = event.key;
        const index = recipients.findIndex((r) => r === activeRecipient);
        if (key === 'ArrowUp' && index > 0) {
            setActiveRecipient(recipients[index - 1]);
        } else if (key === 'ArrowDown' && index < recipients.length - 1) {
            setActiveRecipient(recipients[index + 1]);
        }
        selectRecipient(recipients[index].id);
    };

    const livePreviewInner = () => {
        return (
            <div className="
                    preview-area
                    flex flex-dir-col
                    justify-content-start
                    bg-white
                    max-width-100
                    pad-t-40px pad-r-40px pad-b-40px pad-l-40px
                    overflow-hidden
                " style={{ minHeight: applyExternalStyle ? '' : '90vh' }}>
                <div className={`live-preview__controls flex align-items-center justify-content-end margin-b-16px ${src !== null
                    ? 'flex-wrap' : ''} ${recipients.length > 2 ? 'pad-t-16px' : ''} ${recipients.length < 2 ? 'pad-t-40px' : ''}`}>
                    {
                        src === null && (
                            <h2 className="font-serif font-size-24px font-blue-16 margin-t-0px margin-r-auto margin-b-0px">
                                {landingProps.name || 'Untitled Landing Page'}
                            </h2>
                        )
                    }

                    <p
                        className={`inline-block font-size-16px font-gray-40 text-align-left margin-t-0px margin-r-8px margin-b-0px ${src !== null && recipients.length > 1 ? 'width-100' : ''}`}

                    >
                        Preview as:
                    </p>

                    {
                        recipients.length > 1 && (
                            <>
                                <div className="live-preview__recipient-selector margin-r-8px">
                                    <select
                                        value={activeRecipient.id}
                                        onChange={(e) => {
                                            const selectedIndex = e.target.selectedIndex;
                                            setActiveRecipient(recipients[selectedIndex]);
                                            selectRecipient(recipients[selectedIndex].id);
                                        }}
                                        onKeyDown={handleOptionSelection}
                                    >
                                        {recipients.map((r, index) => (
                                            <option key={`${r.id}-${index}`} value={r.id}>
                                                {`${r.name} ${r.last_name}`}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="focus"></span>
                                </div>

                                <button className="btn btn--icon-only btn--circle margin-r-8px"
                                    onClick={() => scrollRecipient(-1)} aria-label="Preview previous recipient">
                                    <i className="fa fa-chevron-left" />
                                </button>

                                <button className="btn btn--icon-only btn--circle margin-r-auto"
                                    onClick={() => scrollRecipient(1)} aria-label="Preview next recipient">
                                    <i className="fa fa-chevron-right" />
                                </button>
                            </>
                        )
                    }

                    <div className="live-preview__size-selector">
                        {
                            (currentProject.method && currentProject.method.slug === 'text' && applyExternalStyle ? previewSizes.filter((size) => size.label !== 'desktop') : previewSizes).map((size) => (
                                <button key={size.label} className='btn btn--icon-only btn--circle bg-gray-90 margin-l-8px' aria-selected={size.active} aria-label={size.label} onClick={() => setPreviewSize(size)}>
                                    <i className={`fa ${size.iconClass}`} />
                                    <span className="btn__tooltip btn__tooltip--up font-white text-capitalize">{size.label}</span>
                                </button>
                            ))
                        }
                    </div >
                </div>

                {
                    src === null && (
                        <div
                            className="live-preview__container relative width-100 overflow-auto text-align-center pad-b-32px margin-center margin-b-32px"
                            data-size={activeSize().label}
                            ref={containerRef}
                        >
                            <div
                                className="live-preview__bg-img absolute z-idx-1 width-100"
                                style={{ backgroundImage: landingProps.bkgd_image ? `url(${landingProps.bkgd_image.bkgd_image})` : '' }}
                            />

                            <div
                                className="live-preview__header sticky top-0 flex align-items-center"
                                style={{ backgroundColor: landingProps.header_bkgd_color || '#fff' }}
                            >
                                <img
                                    src={headerLogoDisplay()}
                                    alt="Live Preview Header Logo"
                                    role="presentation"
                                    className="live-preview__header-logo"
                                />
                            </div>
                            <div className="live-preview__content relative overflow-hidden z-idx-2">
                                <div className="live-preview__content-gradient" />
                                <div className="live-preview__video-final">
                                    <div className="live-preview__player">
                                        <div className="live-preview__player-play">
                                            <i
                                                className="fa fa-play"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className="btn btn--primary"
                                        style={{
                                            backgroundColor: landingProps.button_color,
                                            color: landingProps.button_text_color,
                                        }}
                                    >
                                        CTA Button
                                    </div>
                                    <div className="live-preview__text-container">
                                        <p className="pad-r-24px pad-l-24px">
                                            This is where your amazing, awesome, stunning, fascinating, wonderful, and prodigious (can we think of any more synoyms?) personalized message will go. Tell your recipients that they&apos;re the greatest folks out there, just like yourself! Use merge fields to really amp up the personalization and keep the fun times rolling. Did we mention that we love unicorns? Fun fact: when a unicorn has wings, they are called Pegasi!
                                        </p>
                                    </div>
                                    <hr className="max-width-200px" />
                                    <div className="live-preview__button-below inline-block margin-t-24px">
                                        <div
                                            className="btn btn--secondary"
                                            style={{
                                                backgroundColor: landingProps.button_reply_color,
                                                color: landingProps.button_below_text_color,
                                            }}
                                        >
                                            Reply
                                        </div>
                                        <div
                                            className="btn btn--primary margin-r-4px margin-l-4px"
                                            style={{
                                                backgroundColor: landingProps.button_save_color,
                                                color: landingProps.button_below_text_color,
                                            }}
                                        >
                                            Save
                                        </div>
                                        <div
                                            className="btn btn--primary-alt"
                                            style={{
                                                backgroundColor: landingProps.button_share_color,
                                                color: landingProps.button_below_text_color,
                                            }}
                                        >
                                            Share
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

                {
                    (src !== null && pageName !== 'setup') && (
                        <div
                            className="live-preview__container live-preview__container--iframe bg-white relative width-100 margin-center"
                            data-size={activeSize().label}
                            ref={containerRef}
                        >
                            {
                                loading && currentProject.landing_page_module !== 'thankgiving' && (
                                    <div
                                        className="live-preview__loading absolute width-100 height-100 flex align-items-center justify-content-center"
                                    >
                                        <i className="fa fa-spinner fa-spin font-size-64px" />
                                    </div>
                                )
                            }
                            <iframe
                                src={src}
                                className="width-100 border-none"
                                id="preview-frame"
                                title="Preview of Your Landing Page"
                                style={activePreviewStyles}
                            />
                        </div>
                    )
                }

                {
                    (src !== null && pageName === 'setup') && (
                        <div
                            className="live-preview__container live-preview__container--email-message bg-white relative width-100 margin-center overflow-auto pad-l-24px pad-r-24px"
                            data-size={activeSize().label}
                            ref={containerRef}
                        >
                            <div className="live-preview__email-preview-header absolute bg-blue-16 width-100 left-0 top-0 pad-t-4px pad-r-8px pad-l-8px" style={{ height: 24 }}>
                                <i className="fa fa-circle font-white"></i>
                                <i className="fa fa-circle font-white"></i>
                                <i className="fa fa-circle font-white"></i>
                                <p className="live-preview__size-label font-size-16px inline-block margin-t-0px text-align-right float-right font-white text-uppercase">{activeSize().label}</p>
                            </div>
                            <div className={`live-preview__email-preview-body absolute right-0 bottom-0 left-0 overflow-auto ${pageName === 'setup' ? 'margin-t-32px' : 'pad-t-88px md-pad-t-104px lg-pad-t-88px'}`}>
                                <EmailPreviewTemplate
                                    activeRecipient={activeRecipient}
                                    business={business}
                                    currentProject={currentProject}
                                    displayFont={displayFont}
                                    emailLogo={emailLogo}
                                    emailMessage={emailMessage}
                                    ctaDesc={ctaDesc}
                                    envelopeName={envelopeName}
                                    envelopePath={envelopePath()}
                                    landingPage={landingPage}
                                    pageName={pageName}
                                    recipients={recipients}
                                    recipientIndex={recipientIndex}
                                    trans={trans}
                                    vrExampleVideo={vrExampleVideo}
                                    withFieldsMerged={withFieldsMerged}
                                />
                            </div>
                        </div>
                    )
                }

                {
                    src === null && (
                        <div
                            className="flex justify-content-space-between width-100"
                        >
                            <button
                                type="button"
                                className="btn btn--secondary height-100"
                                onClick={() => changeStep(step - 1)}
                            >
                                <i className="fas fa-arrow-left" />
                                Back
                            </button>

                            {
                                step === 2 && (
                                    <button
                                        type="button"
                                        className="btn btn--primary height-100"
                                        disabled={submitting}
                                        onClick={saveLanding}
                                    >
                                        {!submitting ? 'Save and Continue' : 'Processing...'}
                                        <i className="fas fa-arrow-right margin-l-4px" />
                                    </button>
                                )
                            }

                            {
                                step === 3 && (
                                    <>
                                        <a
                                            href={`${$config.tvMainAppUrl}/video/landing?landing_id=${existingLandingIdString}`}
                                            className="btn btn--primary-alt height-100"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            <i className="fa fa-eye margin-r-4px" />
                                            Live Preview
                                        </a>

                                        <button
                                            type="button"
                                            className="btn btn--primary height-100"
                                            onClick={newLanding}

                                        >
                                            Create New Landing Page
                                        </button>
                                    </>
                                )
                            }
                        </div>
                    )
                }
            </div >
        );
    };

    return (
        <>
            {
                applyExternalStyle ? (
                    <div className='preview-column fixed bg-purple-95'>
                        {livePreviewInner()}
                    </div>
                ) : (
                    <>
                        {livePreviewInner()}
                    </>
                )
            }
        </>
    );
};


LivePreview.propTypes = {
    pageName: PropTypes.string,
    landingProps: PropTypes.object,
    valid: PropTypes.object,
    emailMessage: PropTypes.string,
    ctaDesc: PropTypes.string,
    src: PropTypes.string,
    step: PropTypes.number,
    submitting: PropTypes.bool,
    existingLandingIdString: PropTypes.string,
    currentProject: PropTypes.object,
    envelope: PropTypes.object,
    envelopeName: PropTypes.string,
    template: PropTypes.string,
    displayFont: PropTypes.object,
    emailTemplate: PropTypes.string,
    emailLogo: PropTypes.string,
    business: PropTypes.object,
    trans: PropTypes.object,
    landingPage: PropTypes.object,
    vrExampleVideo: PropTypes.object,
    applyExternalStyle: PropTypes.bool,
    recipientIndex: PropTypes.number,
    recipients: PropTypes.array,
    withFieldsMerged: PropTypes.func,
    selectRecipient: PropTypes.func,
    saveLanding: PropTypes.func,
};
export default LivePreview;
